import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'

export interface FavoriteGenre {
  id: string
  name: string
  color: string
  count: number
}

export interface UseFavoriteGenreReturn {
  favoriteGenre: FavoriteGenre | null
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to get the user's favorite genre (most common in library)
 */
export function useFavoriteGenre(): UseFavoriteGenreReturn {
  const { user } = useAuth()
  const { i18n } = useTranslation()

  const { data, isLoading, error } = useQuery({
    queryKey: ['favorite-genre', user?.id, i18n.language],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      // Get all books with their genres
      const { data: books, error: booksError } = await supabase
        .from('books')
        .select(`
          id,
          book_genres (
            genre:genres (
              id,
              name,
              name_pt,
              name_en,
              name_es,
              color,
              slug
            )
          )
        `)
        .eq('user_id', user.id)

      if (booksError) throw booksError
      if (!books || books.length === 0) return null

      // Count genres
      const genreCount = new Map<
        string,
        {
          genre: {
            id: string
            name: string
            name_pt?: string
            name_en?: string
            name_es?: string
            color: string
            slug: string
          }
          count: number
        }
      >()

      books.forEach((book) => {
        if (book.book_genres) {
          book.book_genres.forEach((bg) => {
            const genre = Array.isArray(bg.genre) ? bg.genre[0] : bg.genre
            if (genre) {
              const genreId = genre.id
              if (genreCount.has(genreId)) {
                genreCount.get(genreId)!.count++
              } else {
                genreCount.set(genreId, { genre, count: 1 })
              }
            }
          })
        }
      })

      if (genreCount.size === 0) return null

      // Find the most common genre
      const sortedGenres = Array.from(genreCount.values()).sort((a, b) => b.count - a.count)
      const topGenre = sortedGenres[0]

      // Get translated name based on current language
      const getTranslatedName = () => {
        switch (i18n.language) {
          case 'pt':
            return topGenre.genre.name_pt || topGenre.genre.name
          case 'es':
            return topGenre.genre.name_es || topGenre.genre.name
          default:
            return topGenre.genre.name_en || topGenre.genre.name
        }
      }

      return {
        id: topGenre.genre.id,
        name: getTranslatedName(),
        color: topGenre.genre.color,
        count: topGenre.count,
      }
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })

  return {
    favoriteGenre: data || null,
    isLoading,
    error: error as Error | null,
  }
}
